import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import type { Chemical } from '@igus/material-domain';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { AboutThermalProperties } from './AboutThermalProperties';
import { useEffect, useState } from 'react';
import { MaterialChemicalResistance } from './MaterialChemicalResistance';
import { AboutChemicalProperties } from './AboutChemicalProperties';
import { TextInput } from '@hub/common/TextInput/TextInput';
import { useRouter } from 'next/router';
import { getLocale, IbhFilterKeys } from '@hub/libs/constants';
import { useFreeFormHubFilterState } from '../../free-form-hub-result/product-filters/hooks/useUrlFilterState';
import { useFreeFormHubInputValidator } from '../../free-form-hub-result/product-filters/hooks/inputValidator';
import { FilterType } from '../../free-form-hub-result/product-filters/types';
import { MaterialProperties, type moreInfo } from './MaterialProperties';
import { isFootnoteVisible } from '../utils';
import { Dialog } from '@hub/common/Dialog/Dialog';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import type { Material } from '@igus/iglidur-free-form-hub-interfaces';
import { getMaterialDataUrl } from './MaterialData';
import type { UnitSystem } from '@igus/kopla-domain';

type MaterialInfoProps = {
  material: Material;
  unitSystem: UnitSystem;
  setShowMaterialInfo: (value: boolean) => void;
};

export const MaterialInfo = ({ material, setShowMaterialInfo, unitSystem }: MaterialInfoProps) => {
  const { t, i18n } = useTranslation(['category/productFilter', 'common']);
  const { CustomEvent } = usePiwikPro();
  const [chemicals, setChemicals] = useState<Chemical[]>(material.chemicals);
  const [showAboutThermalProperties, setShowAboutThermalProperties] = useState(false);
  const [showAboutChemicalProperties, setShowAboutChemicalProperties] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const selectValidator = useFreeFormHubInputValidator(FilterType.AUTOCOMPLETE);
  const [_, setValue] = useFreeFormHubFilterState(IbhFilterKeys.MATERIAL, selectValidator);

  const router = useRouter();
  const locale = getLocale(router);

  const getDataSheetKey = () => {
    if (locale === 'de') {
      return material.dataSheets?.de;
    }
    return material.dataSheets?.en;
  };

  const openMoreInfo = (info: moreInfo) => {
    if (info === 'thermal') {
      setShowAboutThermalProperties(true);
    } else if (info === 'chemical') {
      setShowAboutChemicalProperties(true);
    }
  };

  // Depending on the material color, to show the appropriate text color on headers
  const isDarkColor = (): boolean => {
    const colorCode = material.colorHex.replace('#', '');

    const r = parseInt(colorCode.substring(0, 2), 16);
    const g = parseInt(colorCode.substring(2, 4), 16);
    const b = parseInt(colorCode.substring(4, 6), 16);

    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    return luminance < 128;
  };

  const materialUrl = getMaterialDataUrl(material, locale, i18n.language);

  useEffect(() => {
    const filteredChemicals = material.chemicals?.filter(chemical => {
      const chemicalName = t(`CHEMICALS.${chemical.name.toUpperCase()}`, {
        ns: 'free-form-hub/chemicals',
      }).toLowerCase();
      return chemicalName.includes(searchValue.toLowerCase());
    });
    setChemicals(filteredChemicals);
  }, [material.chemicals, searchValue]);

  return (
    <>
      <div className="w-screen h-screen overflow-auto">
        <div className="sticky top-0 w-full z-50" style={{ backgroundColor: material.colorHex }}>
          <div className="flex justify-between px-60 py-24">
            <div style={{ color: isDarkColor() ? 'white' : 'black' }}>
              <span className="text-[5.5rem] font-bold">
                {t(`MATERIALS.${material.name}_NAME`, { ns: 'free-form-hub/materials' })}
              </span>
              <div className="font-bold text-sm italic">
                {t(`MATERIALS.${material.name}_DESCRIPTION`, { ns: 'free-form-hub/materials' })}
              </div>
            </div>
            <button
              type="button"
              data-testid="dialog-close-icon"
              onClick={() => {
                setShowMaterialInfo(false);
                CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.articleInfoClose);
              }}
              className={classNames('w-16 h-16')}
            >
              <Icon size={10} color={isDarkColor() ? IconColor.WHITE : IconColor.BLACK} id={IconID.CLOSE_CROSS} />
            </button>
          </div>
        </div>
        <div className="pl-60">
          <MaterialProperties
            material={material}
            openMoreInfo={value => openMoreInfo(value)}
            unitSystem={unitSystem}
            showMoreInfo
          />
        </div>
        <div className="px-60 w-full pt-10">
          <div className="flex justify-between items-center">
            <div className="mt-14 flex items-center text-lg clear-both mb-3">
              <span>{t('MATERIALS-INFO.CHEMICAL_RESISTANCE_TITLE', { ns: 'free-form-hub/materials' })}</span>
              <span
                data-testid="CHEMICAL_RESISTANCE_MORE-INFO"
                onClick={() => openMoreInfo('chemical')}
                className="ml-3 flex cursor-pointer"
              >
                <Icon size={8} color={IconColor.BLACK} id={IconID.INFO_CIRCLE_MODERN} />
              </span>
            </div>
            <div>
              <TextInput
                value={searchValue}
                onChange={value => setSearchValue(value)}
                placeholder={t(`CHEMICAL_SELECTION.SEARCH_PLACEHOLDER`, { ns: 'free-form-hub/chemicals' })}
              />
            </div>
          </div>
          <MaterialChemicalResistance chemicals={chemicals ?? []} searchValue={searchValue} />
        </div>
        <div className="table pl-60">
          <div className="flex text-lg clear-both mb-3">
            <span>{t(`MATERIALS-INFO.ADDITIONAL_INFORMATION_TITLE`, { ns: 'free-form-hub/materials' })}</span>
          </div>
          {!!materialUrl && (
            <div className="table-row text-sm">
              <p className="table-cell min-w-[400px] p-2 pr-10 text-left align-top">
                {t(`MATERIALS-INFO.ADDITIONAL_INFORMATION_TEXT`, { ns: 'free-form-hub/materials' })}

                <a
                  href={getMaterialDataUrl(material, locale, i18n.language)}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-igus-primary"
                >
                  {' '}
                  {t(`MATERIALS-INFO.ADDITIONAL_INFORMATION_URL`, { ns: 'free-form-hub/materials' })}
                </a>
              </p>
            </div>
          )}
        </div>
        <div className="pb-40 leading-[14px] pl-60 text-[10px]">
          {isFootnoteVisible(material, 1) && (
            <div>1) {t(`MATERIALS-INFO.FOOTNOTE_1`, { ns: 'free-form-hub/materials' })} </div>
          )}
          {isFootnoteVisible(material, 2) && (
            <div>2) {t(`MATERIALS-INFO.FOOTNOTE_2`, { ns: 'free-form-hub/materials' })} </div>
          )}
          {isFootnoteVisible(material, 3) && (
            <div>3) {t(`MATERIALS-INFO.FOOTNOTE_3_${material.procedure}`, { ns: 'free-form-hub/materials' })} </div>
          )}
          {isFootnoteVisible(material, 4) && (
            <div>4) {t(`MATERIALS-INFO.FOOTNOTE_4`, { ns: 'free-form-hub/materials' })} </div>
          )}
          {isFootnoteVisible(material, 5) && (
            <div>5) {t(`MATERIALS-INFO.FOOTNOTE_5_${material.procedure}`, { ns: 'free-form-hub/materials' })} </div>
          )}
          {isFootnoteVisible(material, 6) && (
            <div>6) {t(`MATERIALS-INFO.FOOTNOTE_6`, { ns: 'free-form-hub/materials' })} </div>
          )}
        </div>
        <div className="fixed w-full bottom-0 right-60 font-bold pb-5 flex bg-white gap-x-10 items-end justify-end justify-items-end">
          {!!getDataSheetKey() && (
            <a
              href={`https://igus.widen.net/content/${getDataSheetKey()}/pdf/${material.id}_${t(
                `MATERIALS-INFO.DATA_SHEET_FILENAME`,
                { ns: 'free-form-hub/materials' }
              )}.pdf?download=true`}
              className="flex items-center gap-x-3 text-igus-primary px-6 py-5 rounded-sm border border-igus-primary"
            >
              <Icon size={9} color={IconColor.ORANGE} id={IconID.DOWNLOAD} />
              <span
                onClick={() => {
                  CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.articleInfoDownload, material.name);
                }}
              >
                {t('COMMON.DOWNLOAD', { ns: 'free-form-hub/freeFormHubCommon' })}
              </span>
            </a>
          )}
          <button
            onClick={() => {
              setValue([material.id]);
              setShowMaterialInfo(false);
              CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.articleInfoSelect, material.name);
            }}
            type="button"
            className="flex text-white px-6 py-5 rounded-sm bg-igus-primary"
          >
            <span>{t('COMMON.CHOOSE_MATERIAL', { ns: 'free-form-hub/freeFormHubCommon' })}</span>
          </button>
        </div>
      </div>
      {showAboutThermalProperties && (
        <Dialog visible={showAboutThermalProperties} fullScreen>
          <AboutThermalProperties setShowAboutThermalProperties={value => setShowAboutThermalProperties(value)} />
        </Dialog>
      )}

      {showAboutChemicalProperties && (
        <Dialog fullScreen visible={showAboutChemicalProperties}>
          <AboutChemicalProperties setShowAboutChemicalProperties={value => setShowAboutChemicalProperties(value)} />
        </Dialog>
      )}
    </>
  );
};
